import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useMutation, useQuery } from 'react-query'
import { toast } from 'react-toastify'
import posthog from 'posthog-js'
import classNames from 'classnames'
import Header from 'components/headers/withGreenBack'
import DotsLoading from 'components/loadings/dotsLoading'
import { toman } from 'utilities/helper/format'
import {
    calculateSubscriptionPrice,
    initSubscription,
    setSubscriptionPayment,
} from 'services/plan'
import {
    getActiveGateways,
    useActiveGatewayCode,
} from 'utilities/hooks/useIsDefaultGateway'
import { Card, Notice, PrimaryButton, apiMessage } from 'modules/cashbox/ui'
import { useCashbox } from 'modules/cashbox/hooks'
import { rememberPaymentFlow } from 'utilities/analytics/paymentFlow'

/** Server-priced per 50 members, so each option asks the backend. */
function useCashboxPrice(boxId?: string, monthCount?: number) {
    const { data, isLoading } = useQuery(
        ['subscription-price', boxId, monthCount, 'csb'],
        calculateSubscriptionPrice,
        { enabled: !!boxId && !!monthCount }
    )
    return { price: data?.amount, isLoading }
}

/**
 * خرید اشتراک صندوق قرعه‌کشی.
 *
 * Same v2 gateway flow the moneypool uses, with `itemType: 'csb'` — which is
 * what attaches the Order to the Cashbox and lets the payment callback clear
 * the box's `unp` state (`payment/utils.py` moves it to `nct`, or `dft` for a
 * draft box).
 */
export default function CashboxSubscriptionPage() {
    const { id } = useParams()
    const { cashbox } = useCashbox(id)

    const [selectedCode, setSelectedCode] = useState<string | null>(null)
    const [monthCount, setMonthCount] = useState<number | null>(null)

    const profile = JSON.parse(localStorage.getItem('profile') ?? 'null')
    const { data: initData, isLoading: initLoading } = useQuery(
        ['init-subscription', profile?.id],
        initSubscription
    )
    useActiveGatewayCode(initData, setSelectedCode)
    const gateways = getActiveGateways(initData)

    const monthly = useCashboxPrice(id, 1)
    const quarterly = useCashboxPrice(id, 3)
    const yearly = useCashboxPrice(id, 12)
    const pricesLoading =
        monthly.isLoading || quarterly.isLoading || yearly.isLoading

    // An interval with no configured price comes back as 0; a «۰ تومان» card
    // would only fail at the gateway, so it is left out.
    const plans = [
        { months: 1, title: 'اشتراک یک‌ماهه', price: monthly.price },
        { months: 3, title: 'اشتراک سه‌ماهه', price: quarterly.price },
        { months: 12, title: 'اشتراک یک‌ساله', price: yearly.price },
    ].filter((plan) => !!plan.price && plan.price > 0)

    useEffect(() => {
        if (plans.length && !plans.some((plan) => plan.months === monthCount))
            setMonthCount(plans[0].months)
    }, [plans.length, monthCount]) // eslint-disable-line react-hooks/exhaustive-deps

    const selectedPrice = plans.find(
        (plan) => plan.months === monthCount
    )?.price

    // Once, after the prices and the gateway list have both landed — before
    // that the page has nothing on it and «چند بسته دیدی» would read zero for
    // everybody.
    const viewed = useRef(false)
    useEffect(() => {
        if (viewed.current || initLoading || pricesLoading) return
        viewed.current = true
        posthog.capture('subscription_page_viewed', {
            box_id: id,
            box_type: 'csb',
            is_renewal:
                typeof cashbox?.service_package_remaining_days === 'number' &&
                cashbox.service_package_remaining_days > 0,
            remaining_days: cashbox?.service_package_remaining_days ?? null,
            plans_available: plans.length,
            gateways_available: gateways.length,
        })
    }, [initLoading, pricesLoading]) // eslint-disable-line react-hooks/exhaustive-deps

    const pay = useMutation({
        mutationFn: () =>
            setSubscriptionPayment({
                gatewayCode: selectedCode!,
                monthCount: monthCount!,
                ctxId: Number(id),
                amount: selectedPrice!,
                boxType: 'csb',
            }),
        onSuccess: (result: any) => {
            // Stored before the capture: this is what the funds landing reads
            // back to name the flow once the bank returns, and localStorage is
            // synchronous, so it survives the unload regardless.
            rememberPaymentFlow({
                flow_type: 'cashbox_subscription',
                amount: selectedPrice ?? null,
                gateway: selectedCode,
                box_id: id ?? null,
                box_type: 'csb',
                month_count: monthCount,
            })
            // sendBeacon, because `window.location.replace` two lines down
            // unloads the page before an XHR capture gets to leave — this is
            // the last event of the session on the gateway path.
            posthog.capture(
                'payment_gateway_redirect',
                {
                    flow_type: 'cashbox_subscription',
                    cashbox_id: id,
                    month_count: monthCount,
                    gateway: selectedCode,
                },
                { transport: 'sendBeacon' }
            )
            localStorage.setItem(
                'toBePaidTransactions',
                JSON.stringify({ type: 'subscription', data: [] })
            )
            window.location.replace(result?.payment_link)
        },
        onError: (error: any) => {
            posthog.capture('subscription_purchase_failed', {
                flow_type: 'cashbox_subscription',
                cashbox_id: id,
                month_count: monthCount,
                status: error?.response?.status,
            })
            toast.error(apiMessage(error))
        },
    })

    // The dead end this screen can land on: with no priced plan it shows
    // «امکان خرید اشتراک وجود ندارد» and the box stays in `unp` forever. Both
    // counts are needed — no plans and no gateways are different failures with
    // the same blank screen.
    const plansAvailable = plans.length
    const gatewaysAvailable = gateways.length
    useEffect(() => {
        if (initLoading || pricesLoading) return
        posthog.capture('cashbox_subscription_viewed', {
            cashbox_id: id,
            plans_available: plansAvailable,
            gateways_available: gatewaysAvailable,
        })
    }, [initLoading, pricesLoading, id, plansAvailable, gatewaysAvailable])

    if (initLoading) return <DotsLoading />

    return (
        <div className="bg-[#FAFAFA] min-h-screen pb-8">
            <div className="bg-white">
                <Header title="خرید اشتراک صندوق" />
            </div>

            <div className="px-4 pt-3">
                <Card className="mb-3">
                    <p className="text-sm text-text-secondary">
                        صندوق {cashbox?.name}
                    </p>
                    <p className="text-xs text-text-secondary mt-1 leading-5">
                        قیمت اشتراک بر اساس تعداد اعضای صندوق محاسبه می‌شود.
                    </p>
                </Card>

                {pricesLoading && <DotsLoading />}

                {!pricesLoading && plans.length === 0 && (
                    <Notice tone="warn">
                        در حال حاضر امکان خرید اشتراک برای این صندوق وجود
                        ندارد. لطفاً با پشتیبانی همیان تماس بگیرید.
                    </Notice>
                )}

                {plans.map((plan) => (
                    <button
                        key={plan.months}
                        type="button"
                        onClick={() => setMonthCount(plan.months)}
                        className={classNames(
                            'w-full text-right rounded-card border p-3 mb-2 flex items-center justify-between',
                            monthCount === plan.months
                                ? 'border-green-1 bg-[#E6F3DD]'
                                : 'border-divider bg-white'
                        )}
                    >
                        <span
                            className={classNames(
                                'text-sm',
                                monthCount === plan.months
                                    ? 'text-green-1 font-bold'
                                    : 'text-black'
                            )}
                        >
                            {plan.title}
                        </span>
                        <span className="text-sm font-bold text-black">
                            {toman(plan.price!)}
                        </span>
                    </button>
                ))}

                {plans.length > 0 && (
                    <>
                        <p className="text-sm font-bold text-black mt-4 mb-2">
                            درگاه پرداخت
                        </p>
                        {gateways.length === 0 ? (
                            <Notice tone="warn">
                                درگاه پرداخت فعالی در دسترس نیست.
                            </Notice>
                        ) : (
                            gateways.map((gateway) => (
                                <button
                                    key={gateway.unique_code}
                                    type="button"
                                    onClick={() =>
                                        setSelectedCode(gateway.unique_code)
                                    }
                                    className={classNames(
                                        'w-full text-right rounded-card border p-3 mb-2 flex items-center gap-2',
                                        selectedCode === gateway.unique_code
                                            ? 'border-green-1 bg-[#E6F3DD]'
                                            : 'border-divider bg-white'
                                    )}
                                >
                                    {gateway.img_url && (
                                        <img
                                            src={gateway.img_url}
                                            alt=""
                                            className="w-7 h-7 object-contain"
                                        />
                                    )}
                                    <span className="text-sm text-black">
                                        {gateway.unique_code}
                                    </span>
                                </button>
                            ))
                        )}

                        <Card className="mt-3">
                            <div className="flex justify-between items-baseline">
                                <span className="text-sm text-text-secondary">
                                    مبلغ قابل پرداخت
                                </span>
                                <span className="text-base font-bold text-black">
                                    {selectedPrice ? toman(selectedPrice) : '—'}
                                </span>
                            </div>
                        </Card>

                        <div className="mt-4">
                            <PrimaryButton
                                disabled={
                                    !selectedCode ||
                                    !monthCount ||
                                    !selectedPrice ||
                                    pay.isLoading
                                }
                                onClick={() => {
                                    posthog.capture(
                                        'subscription_purchase_initiated',
                                        {
                                            flow_type: 'cashbox_subscription',
                                            amount: selectedPrice,
                                            month_count: monthCount,
                                            cashbox_id: id,
                                            gateway: selectedCode,
                                        }
                                    )
                                    pay.mutate()
                                }}
                            >
                                {pay.isLoading
                                    ? 'در حال انتقال به درگاه…'
                                    : 'پرداخت'}
                            </PrimaryButton>
                        </div>
                    </>
                )}
            </div>
        </div>
    )
}
